import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MessageSquare, Trash2, Search, Filter, Clock, Zap, Briefcase, Home, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODE_ICONS = {
  praca: Briefcase,
  biznes: Zap,
  zycie: Home,
  command: MessageSquare
};

const Conversations = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const MODE_LABELS = {
    praca: t('conversations.modePraca'),
    biznes: t('conversations.modeBiznes'),
    zycie: t('conversations.modeZycie'),
    command: t('conversations.modeCommand')
  };
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  const [stats, setStats] = useState(null);

  const loadConversations = useCallback(async () => {
    try {
      const params = {};
      if (filterMode !== 'all') params.mode = filterMode;
      if (searchQuery) params.search = searchQuery;

      const response = await axios.get(`${API}/conversations/`, {
        params,
        withCredentials: true
      });

      setConversations(response.data.conversations || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load conversations:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [filterMode, searchQuery]);

  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/conversations/stats/summary`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load conversation stats:', error);
      }
    }
  }, []);

  useEffect(() => {
    loadConversations();
    loadStats();
  }, [loadConversations, loadStats]);

  const deleteConversation = async (id) => {
    if (!window.confirm(t('conversations.deleteConfirm'))) return;

    try {
      await axios.delete(`${API}/conversations/${id}`, {
        withCredentials: true
      });
      loadConversations();
      loadStats();
    } catch (error) {
      alert(t('conversations.deleteError'));
    }
  };

  const continueConversation = (conversation) => {
    navigate(`/app?conversation=${conversation.id}`, {
      state: { conversation }
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return t('conversations.timeMinAgo', { count: diffMins });
    if (diffHours < 24) return t('conversations.timeHourAgo', { count: diffHours });
    if (diffDays < 7) return t('conversations.timeDayAgo', { count: diffDays });
    const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-GB' : 'pl-PL';
    return date.toLocaleDateString(locale);
  };

  return (
    <div className="hussar-app-page">
      <header className="hussar-app-header">
        <div className="hussar-app-header-content">
          <Button
            onClick={() => navigate('/app')}
            className="hussar-btn-ghost hussar-btn-icon"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="hussar-app-header-title">
            <MessageSquare className="w-6 h-6" />
            <h1>{t('conversations.headerTitle')}</h1>
          </div>
        </div>
      </header>

      <div className="hussar-app-main">
        <div className="hussar-conversations-container">
          {stats && (
            <div className="hussar-conversations-stats">
              <div className="hussar-stat-item">
                <span className="hussar-stat-label">{t('conversations.statAll')}</span>
                <span className="hussar-stat-value">{stats.total_conversations}</span>
              </div>
              <div className="hussar-stat-item">
                <Briefcase className="w-4 h-4" />
                <span className="hussar-stat-value">{stats.by_mode?.praca || 0}</span>
              </div>
              <div className="hussar-stat-item">
                <Zap className="w-4 h-4" />
                <span className="hussar-stat-value">{stats.by_mode?.biznes || 0}</span>
              </div>
              <div className="hussar-stat-item">
                <Home className="w-4 h-4" />
                <span className="hussar-stat-value">{stats.by_mode?.zycie || 0}</span>
              </div>
            </div>
          )}

          <div className="hussar-conversations-controls">
            <div className="hussar-search-box">
              <Search className="hussar-search-icon" />
              <Input
                type="text"
                placeholder={t('conversations.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="hussar-search-input"
              />
            </div>

            <div className="hussar-filter-buttons">
              <Button
                className={`hussar-filter-btn ${filterMode === 'all' ? 'active' : ''}`}
                onClick={() => setFilterMode('all')}
              >
                {t('conversations.filterAll')}
              </Button>
              <Button
                className={`hussar-filter-btn ${filterMode === 'praca' ? 'active' : ''}`}
                onClick={() => setFilterMode('praca')}
              >
                {t('conversations.filterPraca')}
              </Button>
              <Button
                className={`hussar-filter-btn ${filterMode === 'biznes' ? 'active' : ''}`}
                onClick={() => setFilterMode('biznes')}
              >
                {t('conversations.filterBiznes')}
              </Button>
              <Button
                className={`hussar-filter-btn ${filterMode === 'zycie' ? 'active' : ''}`}
                onClick={() => setFilterMode('zycie')}
              >
                {t('conversations.filterZycie')}
              </Button>
              <Button
                className={`hussar-filter-btn ${filterMode === 'command' ? 'active' : ''}`}
                onClick={() => setFilterMode('command')}
              >
                {t('conversations.filterCommand')}
              </Button>
            </div>
          </div>

          {(() => {
            if (loading) {
              return (
                <div className="hussar-loading">
                  <p>{t('conversations.loading')}</p>
                </div>
              );
            }
            if (conversations.length === 0) {
              return (
                <div className="hussar-empty-state">
                  <MessageSquare className="hussar-empty-icon" />
                  <h3>{t('conversations.empty.title')}</h3>
                  <p>{t('conversations.empty.subtitle')}</p>
                  <p className="hussar-empty-trust">
                    <Lock className="w-3 h-3" />
                    <span>{t('conversations.empty.trust')}</span>
                  </p>
                  <Button
                    onClick={() => navigate('/app')}
                    className="hussar-btn-primary"
                    data-testid="empty-start-conversation-btn"
                  >
                    {t('conversations.empty.cta')}
                  </Button>
                </div>
              );
            }
            return (
              <div className="hussar-conversations-list">
                {conversations.map((conv) => {
                  const ModeIcon = MODE_ICONS[conv.mode] || MessageSquare;
                  return (
                    <Card key={conv.id} className="hussar-conversation-card">
                      <div className="hussar-conversation-header">
                        <div className="hussar-conversation-icon">
                          <ModeIcon className="w-5 h-5" />
                        </div>
                        <div className="hussar-conversation-info">
                          <h3 className="hussar-conversation-title">{conv.title}</h3>
                          <div className="hussar-conversation-meta">
                            <span className="hussar-conversation-mode">
                              {MODE_LABELS[conv.mode]}
                            </span>
                            <span className="hussar-conversation-dot">•</span>
                            <Clock className="w-3 h-3" />
                            <span className="hussar-conversation-time">
                              {formatDate(conv.updated_at)}
                            </span>
                            <span className="hussar-conversation-dot">•</span>
                            <span className="hussar-conversation-count">
                              {t('conversations.messagesCount', { count: conv.message_count })}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="hussar-conversation-actions">
                        <Button
                          onClick={() => continueConversation(conv)}
                          className="hussar-btn-secondary hussar-btn-sm"
                        >
                          {t('conversations.continue')}
                        </Button>
                        <Button
                          onClick={() => deleteConversation(conv.id)}
                          className="hussar-btn-ghost hussar-btn-icon hussar-btn-sm"
                          title={t('conversations.deleteTitle')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default Conversations;